import styled from 'styled-components';
import { FullWidth } from 'ui/utility/fullWidth';
import { SortSelect } from 'ui/indexPages/tables/sortSelect';
import { spacing, colors, backgroundColors } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import { useSortingContext } from 'utils/contexts/sorting';

import { CardItem } from './CardItem';
import { CardsSkeleton } from './CardsSkeleton';
import { GroupSelect } from '../common/GroupSelect';
import { CREATIVE_LIBRARY_COLUMNS } from '../constants';
import { useAssetsQueryContext } from '../../../contexts/AssetsQueryContext';
import { FolderCardItem } from './FolderCardItem';
import { EmptyStateContent } from '../common/EmptyStateContent';
import { useViewContext } from 'modules/creativeLibrary/contexts/ViewContext';

export const StyledUl = styled.ul`
  display: grid;

  /** minmax stops content blowout. */
  /** without it some of the columns may be larger than others */
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-rows: 1fr;
  grid-column-gap: ${spacing.plusHalf};
  grid-row-gap: ${spacing.plusHalf};
  grid-auto-flow: row;
  margin-bottom: 0;
  padding: 0 ${spacing.double};

  @media (max-width: 1350px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 930px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing.plusHalf} 0;
  gap:${spacing.plusHalf};
`;

const CardViewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: ${spacing.single} solid ${colors.borderPrimary};
  border-bottom: ${spacing.single} solid ${colors.borderPrimary};
  padding: ${spacing.half} ${pxToRem(40)};
  background: ${backgroundColors.backgroundPrimary};
`;

const StyledSortSelect = styled(SortSelect)`
  & > p {
    margin: 0;
  }
`;

export const Cards = () => {
  const { sortBy, sortDirection, setSortDirection, setSortBy } = useSortingContext();
  const { assets, folders, isLoading, isPanel } = useAssetsQueryContext();
  const { hasManageBaseTemplatesPermission } = useViewContext();
  const sortOptions = CREATIVE_LIBRARY_COLUMNS.map((column) => ({ ...column, value: column.id }));
  return (
    <FullWidth>
      <CardViewHeader>
        {!hasManageBaseTemplatesPermission || isPanel ? (
          <div />
        ) : (
          <GroupSelect assets={assets} folders={folders} />
        )}
        <StyledSortSelect
          data-test="creative-library-cards-sort-select"
          options={sortOptions}
          handleSort={(value, direction) => {
            setSortDirection(direction);
            setSortBy(value);
          }}
          sortDirection={sortDirection}
          sortedBy={sortBy}
        />
      </CardViewHeader>
      {isLoading ? (
        <CardsSkeleton />
      ) : (
        <>
          <EmptyStateContent />
          {(folders.length > 0 || assets.length > 0) && (
            <CardsContainer>
              <StyledUl>
                {folders.map((folder) => (
                  <FolderCardItem
                    key={`folder-${folder.id}`}
                    folder={folder}
                    isPanel={isPanel}
                    hasManageBaseTemplatesPermission={hasManageBaseTemplatesPermission}
                  />
                ))}
              </StyledUl>
              <StyledUl>
                {assets.map((asset) => (
                  <CardItem
                    key={`asset-${asset.id}`}
                    asset={asset}
                    isPanel={isPanel}
                    hasManageBaseTemplatesPermission={hasManageBaseTemplatesPermission}
                  />
                ))}
              </StyledUl>
            </CardsContainer>
          )}
        </>
      )}
    </FullWidth>
  );
};
